/**
 * RELEASE INFO DISPLAY (read-only)
 *
 * Renders the release info block that appears when viewing an item
 * (not editing). Mirrors the pattern PolishDetailModal has for release
 * fields — same color per category, same layout — so non-polish
 * DetailModals can show the exact same information for parity.
 *
 * Pair with `ReleaseInfoSection` (the editor form) — this is the view
 * side of the same data model.
 */

/** All the release-info fields an item might carry. Everything optional
 *  since items only populate what their releaseType calls for. */
export interface ReleaseInfoItem {
  releaseType?: string;
  collectionName?: string;
  eventName?: string;
  /** Legacy alias for eventName; still on many items pre-migration. */
  eventSource?: string;
  eventTheme?: string;
  boxName?: string;
  /** Legacy alias for boxName on very old items. */
  box?: string;
  boxTheme?: string;
  groupName?: string;
  groupTheme?: string;
  exclusiveTo?: string;
  exclusiveTheme?: string;
  charityName?: string;
  charityCause?: string;
  conventionName?: string;
  collabWith?: string;
  /** Legacy alias for collabWith. */
  collab?: string;
  collabTheme?: string;
  releaseYear?: number | string;
  releaseSeason?: string;
  releaseMonth?: string;
}

interface Props {
  item: ReleaseInfoItem;
  /** When true, adds the releaseType badge label at the top (matches
   *  PolishDetailModal). Non-polish contexts may want to skip it if
   *  they already show it elsewhere. */
  showReleaseTypeBadge?: boolean;
}

export default function ReleaseInfoDisplay({
  item,
  showReleaseTypeBadge = true,
}: Props) {
  const releaseType = item.releaseType || '';
  const eventDisplay = item.eventName || item.eventSource;
  const boxDisplay = item.boxName || item.box;
  const collabDisplay = item.collabWith || item.collab;

  const hasAnyReleaseInfo =
    !!item.collectionName ||
    !!eventDisplay ||
    !!boxDisplay ||
    !!item.groupName ||
    !!item.exclusiveTo ||
    !!item.exclusiveTheme ||
    !!item.charityName ||
    !!item.charityCause ||
    !!item.conventionName ||
    !!collabDisplay ||
    !!item.collabTheme ||
    releaseType === 'core' ||
    releaseType === 'limitedEdition';

  return (
    <div className="flex flex-col gap-2">
      {showReleaseTypeBadge && releaseType && releaseType !== 'unknown' && (
        <div className="text-base font-semibold text-gray-800">
          {releaseType === 'core' && '🏠 Core / Permanent'}
          {releaseType === 'collection' && '📚 Collection'}
          {releaseType === 'event' && '🎪 Event'}
          {releaseType === 'box' && '📦 Box'}
          {releaseType === 'groupCustom' && '👥 Group Custom'}
          {releaseType === 'exclusive' && '⭐ Exclusive'}
          {releaseType === 'charity' && '💝 Charity'}
          {releaseType === 'limitedEdition' && '✨ Limited Edition'}
          {releaseType === 'convention' && '🎫 Convention'}
          {releaseType === 'collab' && '🤝 Collab'}
          {releaseType === 'other' && '📝 Other'}
        </div>
      )}

      {item.collectionName && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">Collection:</span>
          <span className="font-semibold text-purple-700">{item.collectionName}</span>
        </div>
      )}

      {eventDisplay && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">Event:</span>
          <span className="font-semibold text-pink-700">{eventDisplay}</span>
          {item.eventTheme && (
            <span className="text-gray-600">— {item.eventTheme}</span>
          )}
        </div>
      )}

      {boxDisplay && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">Box:</span>
          <span className="font-semibold text-amber-700">{boxDisplay}</span>
          {item.boxTheme && (
            <span className="text-gray-600">— {item.boxTheme}</span>
          )}
        </div>
      )}

      {item.groupName && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">Group:</span>
          <span className="font-semibold text-blue-700">{item.groupName}</span>
          {item.groupTheme && (
            <span className="text-gray-600">— {item.groupTheme}</span>
          )}
        </div>
      )}

      {(item.exclusiveTo || item.exclusiveTheme) && (
        <div className="flex flex-col gap-1">
          {item.exclusiveTo && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Exclusive to:</span>
              <span className="font-semibold text-yellow-700">{item.exclusiveTo}</span>
            </div>
          )}
          {item.exclusiveTheme && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Excl. Theme:</span>
              <span className="font-semibold text-yellow-600">{item.exclusiveTheme}</span>
            </div>
          )}
        </div>
      )}

      {(item.charityName || item.charityCause) && (
        <div className="flex flex-col gap-1">
          {item.charityName && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Charity:</span>
              <span className="font-semibold text-rose-700">{item.charityName}</span>
            </div>
          )}
          {item.charityCause && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Cause:</span>
              <span className="font-semibold text-rose-600">{item.charityCause}</span>
            </div>
          )}
        </div>
      )}

      {item.conventionName && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 uppercase">Convention:</span>
          <span className="font-semibold text-teal-700">{item.conventionName}</span>
        </div>
      )}

      {(collabDisplay || item.collabTheme) && (
        <div className="flex flex-col gap-1">
          {collabDisplay && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Collab With:</span>
              <span className="font-semibold text-violet-700">{collabDisplay}</span>
            </div>
          )}
          {item.collabTheme && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 uppercase">Collab Theme:</span>
              <span className="font-semibold text-violet-600">{item.collabTheme}</span>
            </div>
          )}
        </div>
      )}

      {!hasAnyReleaseInfo && (
        <div className="text-gray-500 italic">No release info</div>
      )}

      {(item.releaseYear || item.releaseSeason || item.releaseMonth) && (
        <div className="flex items-center gap-2 mt-1">
          {item.releaseSeason && (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              item.releaseSeason === 'Spring' ? 'bg-pink-100 text-pink-700' :
              item.releaseSeason === 'Summer' ? 'bg-yellow-100 text-yellow-700' :
              item.releaseSeason === 'Fall' ? 'bg-orange-100 text-orange-700' :
              item.releaseSeason === 'Winter' ? 'bg-blue-100 text-blue-700' :
              'border-2 border-gray-300 text-gray-600'
            }`}>
              {item.releaseSeason === 'Spring' && '🌷 '}
              {item.releaseSeason === 'Summer' && '☀️ '}
              {item.releaseSeason === 'Fall' && '🍂 '}
              {item.releaseSeason === 'Winter' && '❄️ '}
              {item.releaseSeason}
              {item.releaseYear && ` ${item.releaseYear}`}
            </span>
          )}
          {!item.releaseSeason && item.releaseYear && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border-2 border-gray-300 text-gray-600">
              {item.releaseMonth && `${item.releaseMonth} `}{item.releaseYear}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
